import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useRecentlyViewed } from "@/hooks/use-recently-viewed";
import { todayGate } from "@/lib/gates";
import type { ItemKind } from "@/lib/ecosystem";
import { getActivePillar, getPillar, type PillarId } from "@/lib/method";

/**
 * Living Recommendations — a single quiet rail near the bottom of the
 * homepage. Reads recently-viewed items from localStorage and offers three
 * contextual next steps. First-time visitors see the default trio.
 */

interface Suggestion {
  eyebrow: string;
  title: string;
  href: string;
  note: string;
}

function forKind(kind: ItemKind | undefined): Suggestion[] {
  const gate = todayGate();
  const defaults: Suggestion[] = [
    { eyebrow: "Today's Gate", title: gate.title, href: "/books/47-gates-of-consciousness", note: `No. ${gate.n} · from 47 Gates` },
    { eyebrow: "Daily Activation", title: "Enter today's practice", href: "/activations", note: "Breath · sound · discipline" },
    { eyebrow: "The Living Library", title: "Wisdom & teachings", href: "/wisdom", note: "Short teachings for the interior room" },
  ];

  if (kind === "music") {
    return [
      { eyebrow: "Related teaching", title: "Enter the wisdom feed", href: "/wisdom", note: "Follow the frequency into instruction" },
      { eyebrow: "Daily Activation", title: "Sit with the current", href: "/activations", note: "Guided breath and sound" },
      { eyebrow: "Today's Gate", title: gate.title, href: "/books/47-gates-of-consciousness", note: `No. ${gate.n} · from 47 Gates` },
    ];
  }
  if (kind === "wisdom") {
    return [
      { eyebrow: "Meditation", title: "Enter a sitting", href: "/activations", note: "Stillness as architecture" },
      { eyebrow: "Related song", title: "Play a transmission", href: "/music", note: "Sound engineered as instruction" },
      { eyebrow: "Today's Gate", title: gate.title, href: "/books/47-gates-of-consciousness", note: `No. ${gate.n} · from 47 Gates` },
    ];
  }
  if (kind === "activation") {
    return [
      { eyebrow: "Journal prompt", title: "What did you notice?", href: "/account", note: "Bring the sitting into words" },
      { eyebrow: "Today's Gate", title: gate.title, href: "/books/47-gates-of-consciousness", note: `No. ${gate.n} · from 47 Gates` },
      { eyebrow: "Recommended music", title: "Extend the state", href: "/music", note: "Sound that keeps the field open" },
    ];
  }
  if (kind === "book") {
    return [
      { eyebrow: "Companion practice", title: "Enter today's activation", href: "/activations", note: "Read, then sit" },
      { eyebrow: "The Living Library", title: "Related teachings", href: "/wisdom", note: "Short essays for the interior" },
      { eyebrow: "Recommended music", title: "Sound for study", href: "/music", note: "Frequencies to read beneath" },
    ];
  }
  return defaults;
}

export function LivingRecommendations() {
  const recent = useRecentlyViewed(3);
  const last = recent[0];
  const [pillarId, setPillarId] = useState<PillarId | null>(null);
  useEffect(() => { setPillarId(getActivePillar()); }, []);
  const pillar = pillarId ? getPillar(pillarId) : undefined;
  const suggestions = pillar
    ? [
        { eyebrow: `Pillar · ${pillar.name}`, title: pillar.essence, href: `/method/${pillar.id}`, note: pillar.invitation },
        ...forKind(last?.kind).slice(0, 2),
      ]
    : forKind(last?.kind);

  return (
    <section
      aria-labelledby="living-recs"
      className="mx-auto max-w-6xl px-4 pb-24 pt-4 sm:px-6"
    >
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The current suggests</p>
          <h2 id="living-recs" className="mt-2 font-serif text-2xl text-ivory sm:text-3xl">
            {pillar ? `Through the pillar of ${pillar.name.toLowerCase()}.` : last ? "Continue the thread." : "Where the field points next."}
          </h2>
        </div>
        {last ? (
          <p className="hidden text-xs text-muted-foreground sm:block">
            After <span className="text-gold-luminous">{last.title}</span>
          </p>
        ) : null}
      </div>
      <ul className="grid gap-4 sm:grid-cols-3">
        {suggestions.map((s) => (
          <li key={s.href + s.eyebrow}>
            <Link
              to={s.href as never}
              className="group flex h-full flex-col justify-between rounded-2xl border hairline bg-obsidian/60 p-6 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{s.eyebrow}</p>
                <p className="mt-3 font-serif text-xl text-ivory group-hover:text-gold-luminous">
                  {s.title}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">{s.note}</p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-xs text-gold">
                Follow the thread <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
